import * as React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'

import * as priceApi from '../../services/api/price'
import {formDataToPriceRequest, pricingsToSingleFormData} from '../forms/pricings/utils'
import {toValidationSchema} from '../utils/form'

import {Price, PriceFormula, Provider} from '../../types'

import Grid from '../../../../commons/src/components/grid'
import GridCell from '../../../../commons/src/components/grid/GridCell'
import Stack from '../../../../commons/src/components/stack/Stack'

import PricingsForm, {
  SingleFormData,
  getInitialValues,
  validationSchema,
} from '../forms/pricings/form'
import SaveButtonPartial from '../save-button-partial'

import PriceCalculatorPartial from './price-calculator-partial'

type Props = {
  defaultPrice?: Price
  provider: Provider | null
  formula: PriceFormula
  hidePriceCalculator?: boolean
  onDone?: (price: Price) => void
}

const CreatePricingFormPartial = ({
  defaultPrice,
  provider,
  formula,
  hidePriceCalculator = false,
  onDone,
}: Props) => {
  invariant(provider, 'Provider is missing.')

  const formik = useFormik<SingleFormData>({
    initialValues: defaultPrice
      ? {
          ...getInitialValues(formula),
          ...pricingsToSingleFormData(defaultPrice),
        }
      : getInitialValues(formula),
    validationSchema: toValidationSchema(validationSchema),
    enableReinitialize: true,
    onSubmit: async (values, actions) => {
      const priceRequest = formDataToPriceRequest(values, provider)

      try {
        const createdPrice = await priceApi.create(priceRequest)
        if (onDone) {
          onDone(createdPrice)
        }
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  if (hidePriceCalculator) {
    return (
      <Stack space="300">
        <PricingsForm formik={formik} provider={provider} />
        <SaveButtonPartial formik={formik} />
      </Stack>
    )
  }

  return (
    <Grid>
      <GridCell l={6}>
        <Stack space="300">
          <PricingsForm formik={formik} provider={provider} />
          <SaveButtonPartial formik={formik} />
        </Stack>
      </GridCell>
      <GridCell s={0} l={6}>
        <PriceCalculatorPartial
          priceRequest={formDataToPriceRequest(formik.values, provider)}
          provider={provider}
          pricingType={formik.values.pricingType}
        />
      </GridCell>
    </Grid>
  )
}

export default CreatePricingFormPartial
